import { supabase } from '@/lib/supabase/client';
import type { Profile, ProfileUpdate } from '@/lib/supabase/types';

/**
 * Get a profile by user ID
 */
export async function getProfileById(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching profile:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Get a profile by username
 */
export async function getProfileByUsername(
  username: string,
): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('username', username)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching profile:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update a user's profile
 */
export async function updateProfile(
  userId: string,
  updates: ProfileUpdate,
): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Check if a username is available
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', username.toLowerCase())
    .single();

  if (error && error.code === 'PGRST116') {
    // No rows returned means username is available
    return true;
  }

  return !data;
}

/**
 * Update username (with validation)
 */
export async function updateUsername(
  userId: string,
  newUsername: string,
): Promise<Profile> {
  // Validate username format
  const usernameRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  if (!usernameRegex.test(newUsername.toLowerCase())) {
    throw new Error(
      'Username must be alphanumeric with optional hyphens (not at start/end)',
    );
  }

  if (newUsername.length < 3 || newUsername.length > 39) {
    throw new Error('Username must be between 3 and 39 characters');
  }

  // Check availability
  const available = await isUsernameAvailable(newUsername);
  if (!available) {
    throw new Error('Username is already taken');
  }

  return updateProfile(userId, { username: newUsername.toLowerCase() });
}
